import { useEffect, useState } from "react";
import { Box, Card, Checkbox, CircularProgress, Divider, Grid, Typography } from "@mui/joy";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Breadcrumb } from "../../../../components";
import { Alert, Button } from "../../../../shared";
import { useApiResponseParser } from "../../../../hooks";
import { useRoles, useUser, useUserRoles } from "../../../../hooks/query";

interface UserRecord {
    Id: string;
    FirstName: string;
    LastName: string;
    Email: string;
}

interface UserRoleRow {
    Id: string;
    RoleId: string;
    RoleName: string;
}

export default function EditUser() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { parseErrorResponseData } = useApiResponseParser();

    const { useGetUserById } = useUser();
    const { useList: useListRoles } = useRoles();
    const { useListByUser, useSync } = useUserRoles();

    const getUserSvc = useGetUserById();
    const listUserRolesSvc = useListByUser();
    const syncRolesSvc = useSync();
    const { data: allRoles = [], isLoading: rolesLoading } = useListRoles();

    const [user, setUser] = useState<UserRecord | null>(null);
    const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isLoadingUser, setIsLoadingUser] = useState(true);

    useEffect(() => {
        if (!id) return;

        const load = async () => {
            setIsLoadingUser(true);
            setLoadError(null);
            try {
                const [userData, userRoles] = await Promise.all([
                    getUserSvc.mutateAsync({ id }),
                    listUserRolesSvc.mutateAsync({ userId: id }),
                ]);
                setUser(userData as UserRecord);
                setSelectedRoleIds((userRoles as UserRoleRow[]).map((row) => row.RoleId));
            } catch (error) {
                setLoadError(parseErrorResponseData(error) || "Failed to load user.");
            } finally {
                setIsLoadingUser(false);
            }
        };

        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const toggleRole = (roleId: string) => {
        setSelectedRoleIds((prev) =>
            prev.includes(roleId) ? prev.filter((rid) => rid !== roleId) : [...prev, roleId],
        );
    };

    const handleSave = async () => {
        if (!id) return;
        try {
            await syncRolesSvc.mutateAsync({ UserId: id, RoleIds: selectedRoleIds });
            navigate("/admin/users");
        } catch (error) {
            console.error("Failed to save user roles:", error);
        }
    };

    if (isLoadingUser || rolesLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    if (loadError || !user) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage={loadError || "User not found"} />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />
            <Divider sx={{ mb: 2 }} />

            <Box ml={3} maxWidth={760}>
                <Box display="flex" gap={0.5} alignItems="center" mb={1}>
                    <Card sx={{ p: 0.5, borderRadius: "6px" }}>
                        <Shield size={14} />
                    </Card>
                    <Typography level="title-lg">Assign Roles</Typography>
                </Box>
                <Typography level="body-sm">
                    Choose which roles apply to this user. Roles control what areas of the app they can access.
                </Typography>

                <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
                    <Typography level="title-sm">
                        {user.FirstName} {user.LastName}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                        {user.Email}
                    </Typography>
                </Card>

                <Box mt={3}>
                    <Typography level="title-sm" sx={{ mb: 1.5 }}>
                        Roles
                    </Typography>
                    {allRoles.length === 0 ? (
                        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                            No roles are configured yet. Create roles under Admin → Roles first.
                        </Typography>
                    ) : (
                        <Grid container spacing={1}>
                            {allRoles.map((role: any) => {
                                const checked = selectedRoleIds.includes(role.Id);
                                return (
                                    <Grid key={role.Id} xs={12} sm={6}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                cursor: "pointer",
                                                borderColor: checked ? "primary.outlinedBorder" : "divider",
                                                bgcolor: checked ? "primary.softBg" : "background.surface",
                                            }}
                                            onClick={() => toggleRole(role.Id)}
                                        >
                                            <Box display="flex" alignItems="flex-start" gap={1}>
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => toggleRole(role.Id)}
                                                    onClick={(event) => event.stopPropagation()}
                                                    sx={{ mt: 0.25 }}
                                                />
                                                <Box>
                                                    <Typography level="title-sm">{role.Name}</Typography>
                                                    {role.Description ? (
                                                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                                            {role.Description}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>

                <Box mt={3} display="flex" alignItems="center" gap={2}>
                    <Button
                        variant="soft"
                        color="neutral"
                        startDecorator={<ArrowLeft size={18} />}
                        onClick={() => navigate("/admin/users")}
                        disabled={syncRolesSvc.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="soft"
                        startDecorator={<Shield size={18} />}
                        loading={syncRolesSvc.isPending}
                        onClick={handleSave}
                        disabled={syncRolesSvc.isPending}
                    >
                        Save Roles
                    </Button>
                </Box>

                {syncRolesSvc.isError ? (
                    <Box mt={2}>
                        <Alert errorMessage={parseErrorResponseData(syncRolesSvc.error)} />
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
